// Schema-aware relation identity for the resolver's `SchemaSnapshot`.
//
// `SchemaSnapshot.tables` / `.columns` are keyed by `relationKey(qualified)`:
// `schema.name` (both folded) when the relation carries a schema, the bare
// folded name otherwise (SQLite). Two same-named relations in different
// schemas (`sales.orders`, `archive.orders`) therefore never collide, and
// neither do two relations whose delimited names hold a dot (`"a.b".c` vs
// `a."b.c"`, see `identifierKey`). `QualifiedName.foldedName` keeps its
// leaf-name meaning. A key is an identity, never display text or SQL.
//
// A reference taken from SQL text (`FROM orders`, `FROM sales.orders`) is
// resolved with `lookupSnapshotRelation` — never by indexing the maps with a
// folded name:
//   - schema given → that schema's relation. A schemaless snapshot (SQLite)
//     matches by name, so `main.t` is `t`. A schema the snapshot does not hold
//     (Oracle's other owners, MySQL's other databases) resolves to nothing
//     rather than to the connected schema's same-named relation.
//   - no schema → the first `defaultSchemas` entry (PostgreSQL search_path,
//     SQL Server's default schema then `dbo`, the Oracle connected schema, the
//     MySQL database) that holds one. When that list is the session's own
//     (`defaultSchemasExact`), that is the ONLY way a bare name resolves — as
//     on the engine, a table that exists only outside it is not what the bare
//     name means. Otherwise (a fallback guess, or no list at all) the only
//     relation of that name is taken too. Anything else — an ambiguous bare
//     name — resolves to nothing, never to an arbitrary relation.
//
// Lookups go through a per-snapshot name index built from the map VALUES
// (`Table.qualified`, `Column.relation`), so they do not depend on how a
// hand-built snapshot keyed its maps.

import type { QualifiedName } from '../../sql-grammar/model';
import type { AliasBinding, Column, CteBinding, ReachableTable, SchemaSnapshot, Table } from '../types';

export type RelationIdentity = Pick<QualifiedName, 'foldedName' | 'foldedSchema' | 'foldedDatabase'>;

export type SnapshotRelation = {
  readonly key: string;
  readonly qualified: QualifiedName;
  readonly table?: Table;
  readonly columns?: readonly Column[];
};

/**
 * Injective key text for a path of folded identifiers (`schema.table`,
 * `table.column`): the segments joined with `.`, a segment that itself holds
 * a `.` or `"` written `"…"` with its `"` doubled. Every key therefore splits
 * back into exactly its segments — `"a.b".c` and `a."b.c"` stay two keys —
 * while an ordinary `sales.orders` keeps its readable form (picker
 * `data-testid`s are built from these keys). Never SQL, never display text.
 */
export function identifierKey(...segments: readonly string[]): string {
  return segments.map((s) => (/[."]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s)).join('.');
}

/** Map key of a relation in `SchemaSnapshot.tables` / `.columns` (`identifierKey`). */
export function relationKey(relation: RelationIdentity): string {
  return relation.foldedSchema
    ? identifierKey(relation.foldedSchema, relation.foldedName)
    : identifierKey(relation.foldedName);
}

/**
 * Same relation, tolerating a missing schema on either side: a schemaless
 * reference or schemaless data (SQLite, legacy MetaInfo, FK edges without a
 * schema) matches by name, two schemas must agree.
 */
export function sameRelation(a: RelationIdentity, b: RelationIdentity): boolean {
  if (a.foldedName !== b.foldedName) return false;
  if (!a.foldedSchema || !b.foldedSchema) return true;
  return a.foldedSchema === b.foldedSchema;
}

type MutableEntry = { key: string; qualified: QualifiedName; table?: Table; columns?: readonly Column[] };

const indexCache = new WeakMap<SchemaSnapshot, ReadonlyMap<string, readonly SnapshotRelation[]>>();

function relationIndex(snapshot: SchemaSnapshot): ReadonlyMap<string, readonly SnapshotRelation[]> {
  const cached = indexCache.get(snapshot);
  if (cached) return cached;
  const byKey = new Map<string, MutableEntry>();
  // Tolerates partial hand-built snapshots (no `tables`, columns without a
  // `relation`): such an entry is identified by its map key alone.
  for (const [key, table] of snapshot.tables ?? []) byKey.set(key, { key, qualified: table.qualified, table });
  for (const [key, columns] of snapshot.columns ?? []) {
    const existing = byKey.get(key);
    if (existing) {
      existing.columns = columns;
      continue;
    }
    if (columns.length === 0) continue;
    const qualified = columns[0].relation ?? { name: key, raw: key, quoted: false, foldedName: key };
    byKey.set(key, { key, qualified, columns });
  }
  const byName = new Map<string, SnapshotRelation[]>();
  for (const entry of byKey.values()) {
    const bucket = byName.get(entry.qualified.foldedName);
    if (bucket) bucket.push(entry);
    else byName.set(entry.qualified.foldedName, [entry]);
  }
  indexCache.set(snapshot, byName);
  return byName;
}

function otherDatabase(snapshot: SchemaSnapshot, ref: RelationIdentity): boolean {
  if (!ref.foldedDatabase || !snapshot.database) return false;
  return ref.foldedDatabase.toLowerCase() !== snapshot.database.toLowerCase();
}

/**
 * Resolve a relation reference against the snapshot (rules in the header).
 * `preferredSchemas` (folded) overrides the snapshot's `defaultSchemas` for
 * an unqualified reference (`defaultSchemasExact` still applies).
 */
export function lookupSnapshotRelation(
  snapshot: SchemaSnapshot | null | undefined,
  ref: RelationIdentity,
  preferredSchemas?: readonly string[],
): SnapshotRelation | undefined {
  if (!snapshot || otherDatabase(snapshot, ref)) return undefined;
  const candidates = relationIndex(snapshot).get(ref.foldedName);
  if (!candidates || candidates.length === 0) return undefined;
  if (ref.foldedSchema) {
    const exact = candidates.find((c) => c.qualified.foldedSchema === ref.foldedSchema);
    if (exact) return exact;
    const schemaless = candidates.filter((c) => !c.qualified.foldedSchema);
    return schemaless.length === 1 ? schemaless[0] : undefined;
  }
  for (const schema of preferredSchemas ?? snapshot.defaultSchemas ?? []) {
    const hit = candidates.find((c) => c.qualified.foldedSchema === schema);
    if (hit) return hit;
  }
  if (candidates.length !== 1) return undefined;
  // The session's own path decides alone: a relation outside it is not what
  // the bare name means. A schemaless relation has no schema to be outside of.
  const only = candidates[0];
  return snapshot.defaultSchemasExact && only.qualified.foldedSchema ? undefined : only;
}

export function lookupRelationColumns(
  snapshot: SchemaSnapshot | null | undefined,
  ref: RelationIdentity,
): readonly Column[] | undefined {
  return lookupSnapshotRelation(snapshot, ref)?.columns;
}

export function lookupRelationTable(
  snapshot: SchemaSnapshot | null | undefined,
  ref: RelationIdentity,
): Table | undefined {
  return lookupSnapshotRelation(snapshot, ref)?.table;
}

/**
 * The snapshot relation a reference names; the reference itself when the
 * snapshot holds no relation of that name (nothing to disambiguate against);
 * `null` for a bare name the snapshot holds relations of but resolves to none
 * of them — ambiguous, or only outside the session's schema path — which
 * callers must not attribute to any of them (a schemaless reference would
 * match each of them by name).
 */
export function resolvedRelationName(
  snapshot: SchemaSnapshot | null | undefined,
  ref: QualifiedName,
): QualifiedName | null {
  const hit = lookupSnapshotRelation(snapshot, ref);
  if (hit) return hit.qualified;
  if (!snapshot || ref.foldedSchema) return ref;
  return (relationIndex(snapshot).get(ref.foldedName)?.length ?? 0) > 0 ? null : ref;
}

/**
 * A statement's relations as the snapshot relations they name, for MATCHING
 * against snapshot tables (check marks, self-join detection). A bare name the
 * snapshot holds but that does not resolve here — ambiguous, or outside the
 * default schemas — is left out: falling back to the bare name would match
 * another schema's same-named table by name. Names the snapshot does not hold
 * (or no snapshot yet) keep their own spelling, as `resolvedRelationName` does.
 */
export function resolvedRelationsForMatching<T extends { readonly qualified: QualifiedName }>(
  snapshot: SchemaSnapshot | null | undefined,
  relations: readonly T[],
): T[] {
  return relations.flatMap((r) => {
    const qualified = resolvedRelationName(snapshot, r.qualified);
    return qualified ? [{ ...r, qualified }] : [];
  });
}

/** The relation a scope entry stands for: its resolved snapshot relation when known. */
export function reachableRelation(rt: ReachableTable): QualifiedName {
  return rt.resolvedRelation ?? rt.qualified;
}

/** Whether a scope entry (alias / table in FROM) refers to `relation` (e.g. a column's `relation`). */
export function reachableRefersTo(rt: ReachableTable, relation: RelationIdentity): boolean {
  return sameRelation(reachableRelation(rt), relation);
}

const BASE_RELATION_SOURCES: ReadonlySet<AliasBinding['source']> = new Set(['from', 'join', 'using']);

/**
 * The snapshot relation a FROM / JOIN / MERGE USING alias names (schema
 * included) — `ReachableTable.resolvedRelation`. CTE references, derived
 * tables and function sources never resolve to a base table.
 */
export function resolveAliasRelation(
  alias: AliasBinding,
  ctes: readonly CteBinding[],
  snapshot: SchemaSnapshot | null | undefined,
): QualifiedName | undefined {
  if (!snapshot || !BASE_RELATION_SOURCES.has(alias.source) || !alias.target.foldedName) return undefined;
  if (!alias.target.foldedSchema && ctes.some((c) => c.name.foldedName === alias.target.foldedName)) {
    return undefined;
  }
  return lookupSnapshotRelation(snapshot, alias.target)?.qualified;
}
